/**
 * Contenu ARABE (MSA) des trajets de transfert, indexé par slug FR.
 *
 * Jumeau de routesEn.ts : même forme (TransferRouteContent), même clé de jointure
 * (slug FR de routes.ts). Les données structurelles + prix restent uniques
 * (routes.ts + infos.ts) ; on ne traduit ICI que le contenu lisible
 * (lead, highlights, faq, seo). Les canoniques pointent vers /ar avec le slug FR
 * (/ar/chauffeur/transferts/<slug-fr>). Voir localizedRouteContent() dans index.ts.
 */
import type { TransferRouteContent } from "./routes";

/** AR (MSA) content keyed by the FR slug (stable join key). */
export const TRANSFER_ROUTES_AR: Record<string, TransferRouteContent> = {
  "cannes-aeroport-nice": {
    lead: "نقل خاص بين كان ومطار نيس كوت دازور، مع سائق، وتتبّع للرحلات الجوية واستقبال أسفل الطائرة. سعر مقطوع محدّد مسبقًا، دون مفاجآت.",
    highlights: [
      "تتبّع الرحلة لحظيًّا: يعدّل السائق موعد الاستقبال في حال تأخّر رحلتك.",
      "استقبال بلافتة الاسم في الصالة 1 أو 2 بحسب شركة طيرانك.",
      "مسار مباشر عبر الطريق السريع A8، نحو 30 دقيقة خارج أوقات الذروة.",
      "مثالي لمهرجان كان ومعارض MIPIM وMIPCOM والوصول إلى فنادق الكروازيت.",
    ],
    faq: [
      {
        q: "كم تكلفة نقل كان – مطار نيس؟",
        a: "السعر مقطوع ومحدّد مسبقًا حسب فئة المركبة. ترى السعر الدقيق لكل فئة في الجدول أعلاه، دون أي رسوم خفية.",
      },
      {
        q: "هل ينتظر السائق إذا تأخّرت رحلتي؟",
        a: "نعم. نتتبّع رقم رحلتك ونعدّل موعد الاستقبال. تشمل خدمتنا انتظارًا مجانيًا لمدة 30 دقيقة عند الوصول إلى المطار.",
      },
      {
        q: "كم تستغرق الرحلة؟",
        a: "نحو 30 دقيقة خارج أوقات الذروة عبر الطريق السريع A8. احتسب هامشًا إضافيًا خلال مهرجان كان وعطلات نهاية الأسبوع الصيفية.",
      },
    ],
    seo: {
      title: "نقل كان ⇄ مطار نيس — سائق خاص (VTC) | KS Rent Services",
      description:
        "نقل خاص كان – مطار نيس مع سائق، وتتبّع للرحلة وسعر مقطوع. نحو 30 دقيقة، سعر ثابت لكل مركبة. احجز عبر الإنترنت.",
      canonical: "/ar/chauffeur/transferts/cannes-aeroport-nice",
    },
  },
  "monaco-aeroport-nice": {
    lead: "نقل خاص بين موناكو ومطار نيس كوت دازور. سائق بالزيّ الرسمي، وتتبّع للرحلات الجوية وسعر مقطوع، لتنقّلاتك في الأعمال ومغادراتك على متن الطائرات الخاصة.",
    highlights: [
      "خط متكرّر لعملاء الأعمال والمقيمين في الإمارة.",
      "تنسيق مع مهبط مروحيات موناكو متاح عند الطلب.",
      "نحو 35 دقيقة عبر الكورنيش السفلي/الأوسط أو الطريق السريع A8.",
      "تكتّم والتزام بالمواعيد يناسبان الجداول الضيّقة.",
    ],
    faq: [
      {
        q: "ما سعر نقل موناكو – مطار نيس؟",
        a: "سعر مقطوع لكل فئة مركبة، معروض أعلاه. السعر مضمون عند الحجز، شاملًا الرسوم والاستقبال.",
      },
      {
        q: "هل توفّرون الخط إلى مهبط مروحيات موناكو؟",
        a: "نعم، ننسّق عمليات النقل من وإلى مهبط مروحيات موناكو. أشِر إلى ذلك عند الحجز لمواءمة التوقيت.",
      },
      {
        q: "هل الخدمة متاحة في الصباح الباكر أو في وقت متأخّر من الليل؟",
        a: "نعم، رهنًا بالتوفّر، بما في ذلك الرحلات المبكّرة جدًّا أو الليلية. احجز مسبقًا لضمان الموعد.",
      },
    ],
    seo: {
      title: "نقل موناكو ⇄ مطار نيس — سائق خاص (VTC) | KS Rent Services",
      description:
        "نقل خاص موناكو – مطار نيس مع سائق وسعر مقطوع. نحو 35 دقيقة، سعر ثابت لكل مركبة، وتتبّع للرحلة. احجز عبر الإنترنت.",
      canonical: "/ar/chauffeur/transferts/monaco-aeroport-nice",
    },
  },
  "aeroport-nice-saint-tropez": {
    lead: "نقل لمسافات طويلة بين مطار نيس وسان تروبيه، مع سائق خاص. راحة راقية على مدى ساعتين تقريبًا من الطريق، بسعر مقطوع شامل.",
    highlights: [
      "نحو ساعة ونصف إلى ساعتين بحسب حركة المرور الصيفية على A8 ثم طريق الخليج.",
      "يُنصح بمركبات فسيحة (Class V) للأمتعة والمجموعات.",
      "مياه وأجهزة شحن وتكييف مريح على متن السيارة للمسافات الطويلة.",
      "إقبال كبير في الموسم: احجز مبكّرًا للوصول من يونيو إلى سبتمبر.",
    ],
    faq: [
      {
        q: "كم تكلفة نقل مطار نيس – سان تروبيه؟",
        a: "يعتمد السعر المقطوع على فئة المركبة وهو معروض أعلاه. يغطّي الرحلة بالكامل، شاملًا الرسوم والاستقبال، دون أي رسوم مسافة مفاجئة.",
      },
      {
        q: "كم تستغرق الرحلة إلى سان تروبيه؟",
        a: "نحو ساعة ونصف خارج الموسم، وقد تصل إلى ساعتين ونصف في عطلات نهاية الأسبوع الصيفية وذروة حركة المرور في خليج سان تروبيه.",
      },
      {
        q: "أي مركبة أختار لهذا النقل؟",
        a: "للمسافات الطويلة مع الأمتعة، تُعدّ Class V (الفان الفاخر) الأكثر راحة. وتناسب سيارات السيدان Class E وClass S من 2 إلى 3 ركّاب بأمتعة خفيفة.",
      },
    ],
    seo: {
      title: "نقل مطار نيس ⇄ سان تروبيه — سائق خاص | KS Rent Services",
      description:
        "نقل خاص مطار نيس – سان تروبيه مع سائق. نحو ساعة ونصف، بسعر مقطوع شامل لكل مركبة. احجز نقلك إلى الخليج.",
      canonical: "/ar/chauffeur/transferts/aeroport-nice-saint-tropez",
    },
  },
  "aeroport-nice-saint-jean-cap-ferrat": {
    lead: "نقل خاص بين مطار نيس وسان جان كاب فيرا، إحدى أكثر شبه الجزر طلبًا على الريفييرا. سائق متكتّم، واستقبال متقن، وسعر مقطوع.",
    highlights: [
      "يخدم فيلات وفنادق شبه الجزيرة (Grand-Hôtel du Cap-Ferrat، Royal-Riviera…).",
      "رحلة قصيرة نحو 30 دقيقة عبر الكورنيش السفلي.",
      "خدمة تناسب عملاء يبحثون عن الخصوصية.",
      "تنسيق متاح مع حرّاس الفيلات وخدمات الكونسيرج.",
    ],
    faq: [
      {
        q: "ما سعر نقل مطار نيس – سان جان كاب فيرا؟",
        a: "سعر مقطوع لكل فئة، معروض أعلاه، ومضمون عند الحجز. دون رسوم مسافة إضافية أو رسوم استقبال خفية.",
      },
      {
        q: "هل تخدمون الفيلات الخاصة في شبه الجزيرة؟",
        a: "نعم، نتولّى الإنزال والاستقبال مباشرةً عند عنوان الفيلا أو الفندق، بالتنسيق مع خدمة الكونسيرج لديك عند الحاجة.",
      },
      {
        q: "هل الرحلة سريعة من المطار؟",
        a: "نعم، نحو 30 دقيقة عبر الكورنيش السفلي خارج أوقات الذروة. ويمكن اقتراح مسار بانورامي عند الطلب.",
      },
    ],
    seo: {
      title: "نقل مطار نيس ⇄ سان جان كاب فيرا — سائق خاص | KS Rent Services",
      description:
        "نقل خاص مطار نيس – سان جان كاب فيرا مع سائق. نحو 30 دقيقة، سعر مقطوع لكل مركبة، وإنزال عند الفيلا/الفندق. احجز الآن.",
      canonical: "/ar/chauffeur/transferts/aeroport-nice-saint-jean-cap-ferrat",
    },
  },
  "monaco-saint-tropez": {
    lead: "نقل لمسافات طويلة بين موناكو وسان تروبيه مع سائق خاص. قطبان مرموقان يُربطان بكلّ راحة، في رحلة تستغرق نحو ساعتين بسعر مقطوع.",
    highlights: [
      "خط مباشر بين الإمارة وخليج سان تروبيه.",
      "نحو ساعتين عبر الطريق السريع A8 ثم الطريق الساحلي لمنطقة الفار.",
      "يُنصح بـ Class V للراحة والأمتعة في المسافات الطويلة.",
      "مثالي لليخوت والفعاليات والإقامات المجمّعة بين موناكو وسان تروبيه.",
    ],
    faq: [
      {
        q: "كم تكلفة نقل موناكو – سان تروبيه؟",
        a: "يغطّي السعر المقطوع، المعروض أعلاه لكل مركبة، الرحلة بأكملها شاملًا الرسوم والاستقبال. السعر مثبَّت عند الحجز.",
      },
      {
        q: "كم تستغرق رحلة موناكو – سان تروبيه؟",
        a: "نحو ساعتين خارج الموسم، وأكثر خلال ذروة الصيف. نعدّل موعد المغادرة لانسيابية الوصول إلى الخليج.",
      },
      {
        q: "هل يمكن التوقّف في الطريق؟",
        a: "نعم، يمكن التوقّف (غداء، نقطة إطلالة، فندق). أشِر إلى ذلك عند الحجز لإدراجه ضمن مسار النقل.",
      },
    ],
    seo: {
      title: "نقل موناكو ⇄ سان تروبيه — سائق خاص (VTC) | KS Rent Services",
      description:
        "نقل خاص موناكو – سان تروبيه مع سائق. نحو ساعتين، بسعر مقطوع شامل لكل مركبة. خط مرموق على الريفييرا. احجز عبر الإنترنت.",
      canonical: "/ar/chauffeur/transferts/monaco-saint-tropez",
    },
  },
  "cannes-monaco": {
    lead: "نقل خاص بين كان وموناكو مع سائق. خط بارز على الريفييرا، مثالي للفعاليات والأعمال والسهرات في الإمارة.",
    highlights: [
      "نحو ساعة من الطريق عبر A8 بحسب حركة المرور.",
      "مثالي لسباق جائزة موناكو الكبرى والحفلات والعشاءات في الإمارة.",
      "إتاحة السيارة بالساعة على الموقع بديلًا عن رحلة ذهاب بسيطة.",
      "عودة ليلية تُؤمَّن براحة بال تامّة.",
    ],
    faq: [
      {
        q: "ما سعر نقل كان – موناكو؟",
        a: "سعر مقطوع لكل فئة مركبة، معروض أعلاه ومضمون عند الحجز. الرسوم والاستقبال مشمولان، دون أي رسوم مفاجئة.",
      },
      {
        q: "هل توفّرون إتاحة السيارة بالساعة على الموقع في موناكو؟",
        a: "نعم. إلى جانب النقل البسيط، نوفّر إتاحة بالساعة لمواعيدك وسهراتك في الإمارة.",
      },
      {
        q: "هل العودة المتأخّرة من موناكو ممكنة؟",
        a: "نعم، رهنًا بالتوفّر، بما في ذلك بعد حفلٍ أو فعالية. احجز رحلة العودة مسبقًا لضمان السائق.",
      },
    ],
    seo: {
      title: "نقل كان ⇄ موناكو — سائق خاص (VTC) | KS Rent Services",
      description:
        "نقل خاص كان – موناكو مع سائق وسعر مقطوع. نحو ساعة، سعر ثابت لكل مركبة، وإتاحة بالساعة متاحة. احجز عبر الإنترنت.",
      canonical: "/ar/chauffeur/transferts/cannes-monaco",
    },
  },
  "cannes-saint-tropez": {
    lead: "نقل خاص بين كان وسان تروبيه مع سائق. اصِل إلى الخليج بكلّ راحة انطلاقًا من الكروازيت، في رحلة تستغرق نحو ساعة وعشرين دقيقة بسعر مقطوع.",
    highlights: [
      "نحو ساعة وعشرين دقيقة إلى ساعتين بحسب حركة المرور الصيفية نحو خليج سان تروبيه.",
      "يُنصح بـ Class V للمجموعات والأمتعة.",
      "مثالي لإقامات الشاطئ واليخوت والفعاليات في سان تروبيه.",
      "إمكان التوقّف لتناول الغداء أو للتسوّق في الطريق.",
    ],
    faq: [
      {
        q: "كم تكلفة نقل كان – سان تروبيه؟",
        a: "السعر المقطوع لكل مركبة معروض أعلاه، شامل كلّ شيء (الرسوم، الاستقبال). السعر مثبَّت ومضمون منذ الحجز.",
      },
      {
        q: "كم تستغرق رحلة كان – سان تروبيه؟",
        a: "نحو ساعة وعشرين دقيقة خارج الموسم، وقد تصل إلى ساعتين في عطلات نهاية الأسبوع الصيفية. نضبط موعد المغادرة للحدّ من الانتظار في الخليج.",
      },
      {
        q: "أي مركبة للذهاب إلى سان تروبيه؟",
        a: "تُعدّ Class V (الفان الفاخر) مثالية للراحة والمجموعات والأمتعة. وتناسب سيارات السيدان من 2 إلى 3 مسافرين بأمتعة خفيفة.",
      },
    ],
    seo: {
      title: "نقل كان ⇄ سان تروبيه — سائق خاص (VTC) | KS Rent Services",
      description:
        "نقل خاص كان – سان تروبيه مع سائق. نحو ساعة وعشرين دقيقة، بسعر مقطوع شامل لكل مركبة. خط خليج سان تروبيه. احجز.",
      canonical: "/ar/chauffeur/transferts/cannes-saint-tropez",
    },
  },
};
